import { cloneDeep } from 'lodash-es';
import { ProxyItem } from '@/models/proxy';
import { createProxyPageHeaders } from '@/pages/admin/proxy/ProxyPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { proxyService } from '@/services/proxyService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IListParams, IPageOptions } from '@/types';
import { IProxyFilters, IProxyListRowApi, IProxyUpdateResponse } from '@/types/proxy';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'name', order: 'asc' as const }];

function createProxyStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<ProxyItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IProxyFilters>({
        proxySearch: {
            value: null,
            title: 'proxy.filters.proxySearch',
            allowSaveState: true,
        },
        proxyAvailable: {
            value: 'available',
            title: 'proxy.filters.proxyAvailable',
            allowSaveState: true,
            disableClear: true,
        },
        proxyStatus: {
            value: null,
            title: 'proxy.filters.proxyStatus',
            allowSaveState: true,
        },
        proxyBookmakerId: {
            value: null,
            title: 'proxy.filters.proxyBookmakerId',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.proxySearch.value = null;
        filters.proxyAvailable.value = 'available';
        filters.proxyStatus.value = null;
        filters.proxyBookmakerId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createProxyPageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'proxy',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params: IListParams<IProxyFilters> = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Country', 'AuditTable'],
            };

            const response = await proxyService.list(params);
            items.value = response.data.rows.map((row) => new ProxyItem(row));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    function mergeMutationResponse(response: { data: IProxyUpdateResponse }) {
        if (response.data.success === false) return;
        const row = response.data.row;
        if (!row) return;
        const idx = items.value.findIndex((i) => i.id === row.id);
        if (idx !== -1) {
            items.value[idx] = new ProxyItem(row);
        } else {
            items.value = [new ProxyItem(row), ...items.value];
            total.value += 1;
        }
        triggerRef(items);
    }

    function mergeSpeedtestFields(proxyId: number, row: Pick<IProxyListRowApi, 'speed_info'>) {
        const idx = items.value.findIndex((i) => i.id === proxyId);
        if (idx === -1) return;
        items.value[idx].applySpeedFieldsFromApi(row);
        triggerRef(items);
    }

    async function deleteProxy(id: number) {
        const response = await proxyService.delete({ rows: [{ id }] });
        if (response.data.success !== false) {
            await loadData();
        }
    }

    async function restoreProxy(id: number) {
        const response = await proxyService.update({ rows: [{ id, deleted_at: null }] });
        if (response.data.success !== false) {
            await loadData();
        }
    }

    async function patchBoolField(item: ProxyItem, modelKey: 'isActive' | 'isAgent', value: 0 | 1, apiField: string) {
        const oldValue = item[modelKey];
        item[modelKey] = value;
        triggerRef(items);

        try {
            const response = await proxyService.update({ rows: [{ id: item.id, [apiField]: value }] });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success === false) {
                item[modelKey] = oldValue;
                triggerRef(items);
            }
        } catch (e) {
            item[modelKey] = oldValue;
            triggerRef(items);
            throw e;
        }
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        patchBoolField,
        deleteProxy,
        restoreProxy,
        mergeMutationResponse,
        mergeSpeedtestFields,
        hasAdd: true,
        hasRefresh: false,
    };
}

export function useProxyStore(storeId: string = 'proxy') {
    const useStore = defineStore(storeId, () => createProxyStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'proxy'),
    });
    return useStore();
}
